import {
  FiCheck,
  FiAlertTriangle,
  FiLoader,
  FiBarChart2,
  FiClock,
  FiList,
  FiGrid,
} from "react-icons/fi";

const getStatusClass = (status) => {
  switch (status) {
    case "running":
    case "in_progress":
      return "bg-blue-100 text-blue-700 border border-blue-200";
    case "completed":
      return "bg-green-100 text-green-700 border border-green-200";
    case "failed":
      return "bg-red-100 text-red-700 border border-red-200";
    case "pending":
      return "bg-yellow-100 text-yellow-700 border border-yellow-200";
    default:
      return "bg-gray-100 text-gray-700 border border-gray-200";
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "completed":
      return <FiCheck className="mr-1.5 w-4 h-4" />;
    case "failed":
      return <FiAlertTriangle className="mr-1.5 w-4 h-4" />;
    case "in_progress":
    case "running":
      return <FiLoader className="mr-1.5 w-4 h-4 animate-spin" />;
    case "pending":
      return <FiClock className="mr-1.5 w-4 h-4" />;
    default:
      return <FiBarChart2 className="mr-1.5 w-4 h-4" />;
  }
};

// Format cron expression into human-readable text
const formatCronExpression = (cronExpression) => {
  if (!cronExpression) return "Not scheduled";

  const parts = cronExpression.split(" ");
  if (parts.length !== 5) return cronExpression;

  // Common patterns
  if (
    parts[0] === "0" &&
    parts[1] === "0" &&
    parts[2] === "*" &&
    parts[3] === "*" &&
    parts[4] === "*"
  ) {
    return "Daily at midnight";
  }

  if (
    parts[0] === "0" &&
    parts[1] === "12" &&
    parts[2] === "*" &&
    parts[3] === "*" &&
    parts[4] === "*"
  ) {
    return "Daily at noon";
  }

  if (
    parts[0] === "0" &&
    parts[2] === "*" &&
    parts[3] === "*" &&
    parts[4] === "*"
  ) {
    return `Daily at ${parts[1]}:00`;
  }

  if (
    parts[0] === "0" &&
    parts[1] === "0" &&
    parts[2] === "*" &&
    parts[3] === "*" &&
    parts[4] === "1"
  ) {
    return "Every Monday at midnight";
  }

  return cronExpression;
};

// InfoPanel component to explain workflows
const InfoPanel = ({ onClose }) => {
  return (
    <div className="bg-white border border-primary/20 rounded-xl shadow-sm p-6 mb-6 relative overflow-hidden transition-all hover:shadow-md">
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -z-10"></div>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        aria-label="Close information panel"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <div className="flex items-center justify-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Understanding Workflows
          </h3>
          <p className="text-gray-600 mb-4">
            Workflows help you automate document processing by defining
            sequential stages with parallel execution jobs.
          </p>

          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <div className="bg-gray-50 p-4 rounded-lg flex items-start">
              <div className="mr-3 text-primary">
                <FiList className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800">Sequential Stages</h4>
                <p className="text-sm text-gray-600">
                  Stages execute one after another in a predefined order
                </p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg flex items-start">
              <div className="mr-3 text-primary">
                <FiGrid className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800">Parallel Jobs</h4>
                <p className="text-sm text-gray-600">
                  Multiple jobs run simultaneously within each stage
                </p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg flex items-start">
              <div className="mr-3 text-primary">
                <FiClock className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800">Custom Frequency</h4>
                <p className="text-sm text-gray-600">
                  Set how often each job should run based on your needs
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { getStatusClass, getStatusIcon, formatCronExpression, InfoPanel };
